import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormCheck, FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import ImageColorForm from "go-app/components/ImageForm/ImageColorForm";
import handleError from "go-app/services/errors";
import { UnprocessableEntity } from "go-core/api/exceptions";
import { FormDirty } from "go-form/components/FormDirty";
import FormNumberInput from "go-form/components/FormNumberInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { apiOrganization } from "../../../../../../../../../../../services/Api/Organization/apiOrganization";
import { InvoiceDetailsFormType } from "../../../../../../../../../../../services/Api/Organization/types";
import { InvoiceDetailsFormProps } from "../utils/types";
import { IntegrationWithKSeFModal } from "./KSeF/IntegrationWithKSeFModal";
import { KSeFIntegrationCard } from "./KSeF/KSeFIntegrationCard";

const InvoiceDetailsForm = ({
	details,
	setInvoiceDetails,
	onFetchInvoiceDetails,
}: InvoiceDetailsFormProps): JSX.Element => {
	const form = useForm<InvoiceDetailsFormType>({
		criteriaMode: "all",
		defaultValues: {
			...details,
			ksef_crt_auth_cert_file: undefined,
			ksef_key_auth_cert_file: undefined,
			ksef_crt_offline_cert_file: undefined,
			ksef_key_offline_cert_file: undefined,
			accept_store_ksef_sensitive_data_terms: details.accepted_store_ksef_sensitive_data_terms,
			ksef_environment_type: details.ksef_environment_type ?? "PROD",
		},
	});
	const [loading, setLoading] = useState(false);
	const [showIntegrationWithKSeFModal, setShowIntegrationWithKSeFModal] = useState(false);
	const { addFlash, addSuccessFlash } = useFlash();
	const [newAvatar, setNewAvatar] = useState<string | undefined>(details.logo_link?.default_link);
	const KSeFInstalled = details.ksef_token === "*****" || Boolean(details.ksef_auth_cert);
	const {
		register,
		handleSubmit,
		formState: { errors },
		getValues,
		reset,
		setError,
		formState,
		control,
		watch,
	} = form;
	const { t } = useTranslation();

	const onSuccessSubmit = async (data: InvoiceDetailsFormType) => {
		setLoading(true);
		if (getValues("logo.image_data") || !newAvatar) {
			data.logo_link = undefined;
		}

		try {
			const formData = new FormData();
			if (data.ksef_crt_offline_cert_file)
				formData.append("ksef_crt_offline_cert_file", data.ksef_crt_offline_cert_file);
			if (data.ksef_key_offline_cert_file)
				formData.append("ksef_key_offline_cert_file", data.ksef_key_offline_cert_file);
			if (data.ksef_crt_auth_cert_file) formData.append("ksef_crt_auth_cert_file", data.ksef_crt_auth_cert_file);
			if (data.ksef_key_auth_cert_file) formData.append("ksef_key_auth_cert_file", data.ksef_key_auth_cert_file);

			delete data.ksef_crt_offline_cert_file;
			delete data.ksef_key_offline_cert_file;
			delete data.ksef_crt_auth_cert_file;
			delete data.ksef_key_auth_cert_file;
			formData.append("dto", new Blob([JSON.stringify(data)], { type: "application/json" }));
			const res = await apiOrganization.updateInvoiceDetails(formData);
			reset({
				...res,
				ksef_crt_offline_cert_file: undefined,
				ksef_key_offline_cert_file: undefined,
				ksef_crt_auth_cert_file: undefined,
				ksef_key_auth_cert_file: undefined,
			});
			setInvoiceDetails(res);
			addSuccessFlash(t("lib:common.flash.saved"));
			onHideModal();
		} catch (e) {
			handleError.form(e, setError, addFlash);
			if (e instanceof UnprocessableEntity) {
				const fileError = e.errors.find(
					(error) => error.code === "invalid_ksef_offline_cert" || error.code === "invalid_ksef_auth_cert"
				);
				if (fileError) {
					const fieldName =
						fileError.code === "invalid_ksef_auth_cert"
							? "ksef_crt_auth_cert_file"
							: "ksef_crt_offline_cert_file";
					setError(fieldName, { message: t(`constraints.${fileError.code}`) });
				}
			}
		}
		setLoading(false);
	};

	const onErrorSubmit = (formErrors: Record<string, any>) => {
		Object.entries(formErrors).forEach(([key, value]) => {
			if (key === "accept_store_ksef_sensitive_data_terms") addFlash({ type: "danger", msg: value.message });
		});
	};

	const onSubmit = handleSubmit(onSuccessSubmit, onErrorSubmit);

	const handleImageChange = (imageBase: string | undefined) => {
		setNewAvatar(imageBase);
	};

	const numerationTypes = [
		{ value: "NUMBER_YEAR", label: t("enums.invoices.numeration.NUMBER") },
		{ value: "NUMBER_MONTH_YEAR", label: t("enums.invoices.numeration.NUMBER_MONTH_YEAR") },
	];

	const onHideModal = () => setShowIntegrationWithKSeFModal(false);

	return (
		<FormDirty loading={loading} formState={formState} onSubmit={onSubmit}>
			{details.ksef_visible && (
				<div className="row">
					<div className="col-md-10">
						<KSeFIntegrationCard
							setShowIntegrationWithKSeFModal={setShowIntegrationWithKSeFModal}
							form={form}
							onSubmit={onSubmit}
							KSeFInstalled={KSeFInstalled}
							KSeFTestInvoiceCount={details.ksef_test_invoices_count}
							KSeFEnvironment={details.ksef_environment_type}
							onFetchInvoiceDetails={onFetchInvoiceDetails}
						/>
					</div>
				</div>
			)}
			<fieldset className="form-group no-gutters">
				<div className={"row"}>
					<div className={"col-md-10"}>
						<div className={"row row-cols-md-2"}>
							<div className={"col-md-6"}>
								<FormSelectGroup
									label={t("modules.invoice_details.field.numeration_type.title")}
									name="numeration_type"
									defaultValue={details.numeration_type}
									errors={errors}
									options={numerationTypes}
									control={control}
									data-testid="numeration_type"
								/>
							</div>
							<div className={"col-md-6"}>
								<FormInput
									label={t("modules.invoice_details.field.place_of_issue.title")}
									register={register}
									name="place_of_issue"
									errors={errors}
								/>
							</div>
							<div className={"col-md-6"}>
								<FormInput
									label={t("modules.invoice_details.field.signed_by.title")}
									register={register}
									name="signed_by"
									errors={errors}
								/>
							</div>
							<div className=" d-flex align-items-end mb-1">
								<FormCheck
									name="signed_by_logged_user"
									register={register}
									errors={errors}
									type="switch"
									label={t("modules.invoice_details.field.signed_by_logged_user.title")}
								/>
							</div>
							<div className={"col-md-6"}>
								<FormInput
									label={t("modules.invoice_details.field.default_comment.title")}
									register={register}
									name="default_comment"
									errors={errors}
								/>
							</div>
							<div className={"col-md-6"}>
								<FormInput
									label={t("modules.invoice_details.field.invoice_prefix.title")}
									register={register}
									name="prefix"
									errors={errors}
								/>
							</div>
							<div className={"col-md-6"}>
								<FormInput
									label={t("modules.invoice_details.field.invoice_suffix.title")}
									register={register}
									name="suffix"
									errors={errors}
								/>
							</div>
							<div className={"col-md-6"}>
								<FormInput
									label={t("modules.invoice_details.field.correction_prefix.title")}
									register={register}
									name="correction_prefix"
									errors={errors}
								/>
							</div>
							<div className={"col-md-6"}>
								<FormInput
									label={t("modules.invoice_details.field.correction_suffix.title")}
									register={register}
									name="correction_suffix"
									errors={errors}
								/>
							</div>
							<div className={"col-md-6"}>
								<FormInput
									label={t("modules.invoice_details.field.proforma_prefix.title")}
									register={register}
									name="proforma_prefix"
									errors={errors}
								/>
							</div>
							<div className={"col-md-6"}>
								<FormInput
									label={t("modules.invoice_details.field.proforma_suffix.title")}
									register={register}
									name="proforma_suffix"
									errors={errors}
								/>
							</div>
							<div className={"col-md-6"}>
								<FormInput
									label={t("modules.invoice_details.field.advance_prefix.title")}
									register={register}
									name="advance_prefix"
									errors={errors}
								/>
							</div>
							<div className={"col-md-6"}>
								<FormInput
									label={t("modules.invoice_details.field.advance_suffix.title")}
									register={register}
									name="advance_suffix"
									errors={errors}
								/>
							</div>
							<div className={"col-md-6"}>
								<FormNumberInput
									control={control}
									errors={errors}
									decimalScale={1}
									suffix={""}
									name={"first_invoice_number"}
									label={t("modules.invoice_details.field.first_invoice_number.title")}
								/>
							</div>
							<div className={"col-md-6"}>
								<FormNumberInput
									control={control}
									errors={errors}
									decimalScale={1}
									suffix={""}
									name={"first_proforma_number"}
									label={t("modules.invoice_details.field.first_proforma_number.title")}
								/>
							</div>
							<div className={"col-md-6"}>
								<FormNumberInput
									control={control}
									errors={errors}
									decimalScale={1}
									suffix={""}
									name={"first_advance_number"}
									label={t("modules.invoice_details.field.first_advance_number.title")}
								/>
							</div>
							<div className={"col-md-6"}>
								<FormNumberInput
									control={control}
									errors={errors}
									decimalScale={1}
									suffix={""}
									name={"first_correction_number"}
									label={t("modules.invoice_details.field.first_correction_number.title")}
								/>
							</div>
							<div className="col-md-6">
								<FormCheck
									name="exclude_tip_from_grouping"
									register={register}
									errors={errors}
									type="switch"
									label={t("modules.invoice_details.field.exclude_tip_from_grouping.title")}
								/>
							</div>
							<div className="col-md-6">
								<FormCheck
									name="print_payment_due"
									register={register}
									errors={errors}
									type="switch"
									label={t("modules.invoice_details.field.print_payment_due.title")}
								/>
							</div>
							<div className="col-md-6">
								<FormCheck
									name="separate_advance_correction_numeration"
									register={register}
									errors={errors}
									type="switch"
									label={t(
										"modules.invoice_details.field.separate_advance_correction_numeration.title"
									)}
								/>
							</div>
							{watch("separate_advance_correction_numeration") && (
								<>
									<div className={"col-md-6"}>
										<FormNumberInput
											control={control}
											errors={errors}
											decimalScale={1}
											suffix={""}
											name={"first_advance_correction_number"}
											label={t(
												"modules.invoice_details.field.first_advance_correction_number.title"
											)}
										/>
									</div>
									<div className={"col-md-6"}>
										<FormInput
											label={t("modules.invoice_details.field.advance_correction_prefix.title")}
											register={register}
											name="advance_correction_prefix"
											errors={errors}
										/>
									</div>
									<div className={"col-md-6"}>
										<FormInput
											label={t("modules.invoice_details.field.advance_correction_suffix.title")}
											register={register}
											name="advance_correction_suffix"
											errors={errors}
										/>
									</div>
								</>
							)}
						</div>
					</div>
					<div className={"col-md-2"}>
						<ImageColorForm
							form={form}
							handleSave={handleImageChange}
							prefix="logo"
							mode="IMAGE"
							data={details}
							resourceLink={details.logo_link}
							resourceData={details.logo}
						/>
					</div>
				</div>
			</fieldset>
			<IntegrationWithKSeFModal
				show={showIntegrationWithKSeFModal}
				form={form}
				onHide={onHideModal}
				onSubmit={onSubmit}
				loading={loading}
				KSeFInstalled={KSeFInstalled}
				KSeFTermsAccepted={details.accepted_store_ksef_sensitive_data_terms}
			/>
		</FormDirty>
	);
};
export default InvoiceDetailsForm;
